import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import { getProducts, getCategoryIdBySlug, CATEGORY_MAP } from '../api/woocommerce';

const HERO_IMAGES = {
  'canne-da-pesca': '/hero-canne.png',
  'monofili': '/hero-monofili.png',
  'accessori': '/hero-accessori.png',
  'line': '/hero-line.png',
  'tools': '/hero-line.png',
  'default': '/hero-canne.png',
};
const TITLE_MAP = {
  'canne-da-pesca': 'Canne\nda pesca', 'monofili': 'Monofili', 'accessori': 'Accessori\nda pesca',
  'bolognesi': 'Bolognesi', 'surf-casting': 'Surf Casting', 'spinning': 'Spinning',
  'eging': 'Eging', 'slow-pitch': 'Slow Pitch', 'boat-trolling': 'Boat & Trolling',
  'line': 'Line', 'tools': 'Tools',
};
const DESC_MAP = {
  'canne-da-pesca': 'Esplora la nostra gamma di canne da pesca, progettate per offrire precisione, resistenza e massime prestazioni. Che tu sia alle prime armi o un pescatore esperto, troverai modelli adatti a ogni tecnica e ambiente, dalla pesca in mare a quella in acque interne. Scegli tra materiali innovativi e design ergonomici per vivere ogni uscita con il massimo controllo e comfort.',
  'bolognesi': 'Punto di riferimento per un pubblico di pescatori esigenti e appassionati, Falcon unisce innovazione e tradizione, offrendo soluzioni tecniche di alto livello per diverse discipline della pesca, su tutte la "Bolognese".',
  'surf-casting': 'Soluzioni sviluppate per garantire precisione nel lancio, affidabilità e controllo nelle diverse condizioni di pesca dalla spiaggia.',
  'spinning': 'Canne pensate per assicurare sensibilità, precisione e gestione efficace dell\'artificiale durante tutta l\'azione di pesca.',
  'eging': 'Canne sviluppate tramite uno studio approfondito sulle dinamiche di lancio e recupero dei cefalopodi.',
  'slow-pitch': 'Attrezzature progettate per accompagnare con precisione il movimento del jig e mantenere controllo durante ogni fase dell\'azione.',
  'boat-trolling': 'Affidabili e robuste, le proposte di Falcon per la traina nascono per rispondere alle esigenze della pesca dalla barca e della traina.',
  'line': 'Da oltre 40 anni, Falcon si impegna nella ricerca e selezione dei materiali per garantire prestazioni costanti e affidabili.',
  'tools': 'Linea di foderi che garantiscono il massimo della protezione alle vostre canne Falcon.',
};
const SUBCATS = {
  'canne-da-pesca': ['Bolognesi','Surf Casting','Spinning','Eging','Slow Pitch','Boat & Trolling'],
  'default': [],
};
// Quando il filtro è "Tutti", caricare da queste sottocategorie invece che dalla parent
const MULTI_SUBCATS = {
  'canne-da-pesca': ['bolognesi', 'surf-casting', 'spinning', 'eging', 'slow-pitch', 'boat-trolling'],
};

// Cache degli ID a livello di modulo: persiste per tutta la sessione
const catIdCache = new Map();
const getCategoryIdCached = (s) => {
  if (catIdCache.has(s)) return Promise.resolve(catIdCache.get(s));
  return getCategoryIdBySlug(s).then(id => { if (id) catIdCache.set(s, id); return id; });
};

export default function CategoriaPage() {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeFilter = searchParams.get('filtro') || 'Tutti';
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [categoryId, setCategoryId] = useState(null);
  const [activeCatId, setActiveCatId] = useState(null);
  // null = in attesa; [] = non serve multi-subcat; [id,...] = pronti
  const [subcatIds, setSubcatIds] = useState(null);

  const heroImg = HERO_IMAGES[slug] || HERO_IMAGES['default'];
  const title = TITLE_MAP[slug] || slug;
  const desc = DESC_MAP[slug] || null;
  const subcats = SUBCATS[slug] || SUBCATS['default'];
  const allFilters = subcats.length > 0 ? [...subcats] : [];

  // Pre-risolvi gli ID delle sottocategorie appena lo slug è noto
  useEffect(() => {
    const multiSlugs = MULTI_SUBCATS[slug];
    if (!multiSlugs) { setSubcatIds([]); return; }
    Promise.all(multiSlugs.map(getCategoryIdCached))
      .then(ids => setSubcatIds(ids.filter(Boolean)))
      .catch(() => setSubcatIds([]));
  }, [slug]);

  useEffect(() => { getCategoryIdCached(slug).then(setCategoryId).catch(() => setCategoryId(null)); }, [slug]);

  const loadProducts = useCallback(async (pageNum = 1, reset = false, catId = null) => {
    setLoading(true);
    try {
      let data;
      if (catId === null && subcatIds && subcatIds.length > 0) {
        // ID già risolti: un solo round di chiamate prodotti in parallelo
        const results = await Promise.all(
          subcatIds.map(id => getProducts({ category: id, perPage: 100, page: 1 }))
        );
        const seen = new Set();
        data = [];
        for (const arr of results) {
          for (const p of arr) {
            if (!seen.has(p.id)) { seen.add(p.id); data.push(p); }
          }
        }
        setHasMore(false);
      } else {
        const params = { perPage: 12, page: pageNum };
        const effectiveId = catId !== null ? catId : categoryId;
        if (effectiveId) params.category = effectiveId;
        data = await getProducts(params);
        setHasMore(data.length === 12);
      }
      setProducts(prev => reset ? data : [...prev, ...data]);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [categoryId, subcatIds]);

  useEffect(() => {
    if (subcatIds === null) return; // aspetta che gli ID siano pronti
    setPage(1);
    if (activeFilter && activeFilter !== 'Tutti') {
      const subSlug = CATEGORY_MAP[activeFilter];
      if (subSlug) {
        getCategoryIdCached(subSlug).then(id => {
          setActiveCatId(id);
          loadProducts(1, true, id);
        }).catch(() => { setActiveCatId(null); loadProducts(1, true); });
        return;
      }
    }
    setActiveCatId(null);
    loadProducts(1, true);
  }, [categoryId, activeFilter, loadProducts, subcatIds]);

  return (
    <>
      <style>{`
        .cat-page-hero { position: relative; height: 50vh; min-height: 462px; overflow: hidden; background: #000; display: flex; flex-direction: column; }
        .cat-page-hero-bg { position: absolute; inset: 0; object-fit: cover; object-position: center center; width: 100%; height: 100%; opacity: 0.65; }
        .cat-page-hero-overlay { position: absolute; inset: 0; background: linear-gradient(to bottom, rgba(0,0,0,0.3), #000); }
        .cat-page-hero-content { position: relative; z-index: 10; padding: 140px 138px 60px; flex: 1; display: flex; flex-direction: column; }
        .cat-page-hero-inner { display: flex; flex-direction: row; align-items: stretch; gap: 22px; flex: 0 0 auto; }
        .cat-page-hero-line { width: 2px; background: #d71921; flex-shrink: 0; }
        .cat-page-hero-text { display: flex; flex-direction: column; justify-content: flex-start; gap: 24px; }
        .cat-page-hero-title { font-family: 'Alumni Sans',sans-serif; font-weight: 500; font-size: 100px; line-height: 85px; color: #fff; margin: 0; white-space: pre-line; }
        .cat-page-hero-sub { font-family: 'Roboto',sans-serif; font-weight: 300; font-size: 21px; color: rgba(255,255,255,0.85); line-height: 1.65; max-width: 480px; margin: 0; }
        .cat-page-section { background: #000; padding: 0 0 80px; }
        .cat-page-filters { display: flex; gap: clamp(8px, 4vw, 82px); padding: 32px 20px; flex-wrap: wrap; justify-content: center; border-bottom: 1px solid #222; margin-bottom: 28px; }
        .filter-btn { font-family: 'Alumni Sans',sans-serif; font-weight: 600; font-size: 24px; padding: 14px 38px; cursor: pointer; border: none; transition: all 0.2s; }
        .filter-active { background: #d71921; color: #fff; }
        .filter-inactive { background: #181818; color: rgba(255,255,255,0.5); }
        .filter-inactive:hover { color: #fff; }
        .cat-page-grid { display: grid; grid-template-columns: repeat(4,1fr); margin: 0 138px; background: #000; gap: 12px; }
        .cat-page-load-more { display: block; width: calc(100% - 276px); margin: 36px 138px 0; background: #d71921; color: #fff; font-family: 'Alumni Sans',sans-serif; font-weight: 600; font-size: 20px; padding: 16px; cursor: pointer; border: none; text-align: center; transition: opacity 0.2s; }
        .cat-page-load-more:hover { opacity: 0.85; }
        .cat-page-loading { text-align: center; padding: 80px; font-family: 'Alumni Sans',sans-serif; font-size: 26px; color: rgba(255,255,255,0.4); }
        .cat-page-empty { text-align: center; padding: 80px 20px; font-family: 'Alumni Sans',sans-serif; font-size: 22px; color: rgba(255,255,255,0.4); }

        @media (max-width: 1300px) {
          .cat-page-hero-content { padding: 130px 80px 50px; }
          .cat-page-hero-title { font-size: 66px; line-height: 58px; }
          .cat-page-hero-text { justify-content: flex-start; gap: 28px; }
          .cat-page-hero-inner { flex: 0 0 auto; }
          .filter-btn { font-size: 21px; padding: 12px 30px; }
        }
        @media (max-width: 1100px) {
          .cat-page-hero-content { padding: 120px 60px 50px; }
          .cat-page-filters { padding: 24px 40px 16px; }
          .filter-btn { font-size: 19px; padding: 11px 22px; }
          .cat-page-grid { grid-template-columns: repeat(3,1fr); margin: 0 60px; background: #000; gap: 12px; }
          .cat-page-load-more { width: calc(100% - 120px); margin: 28px 60px 0; }
        }
        @media (max-width: 900px) {
          .cat-page-hero { min-height: 0; height: auto; }
          .cat-page-hero-content { padding: 130px 20px 30px; }
          .cat-page-hero-title { font-size: 68px; line-height: 60px; }
          .cat-page-hero-sub { font-size: 20px; }
          .cat-page-hero-text { gap: 20px; }
          .cat-page-filters { padding: 20px 16px 14px; gap: 10px; }
          .filter-btn { font-size: 18px; padding: 10px 20px; }
          .cat-page-grid { grid-template-columns: repeat(2,1fr); margin: 0 20px; background: #000; gap: 12px; }
          .cat-page-load-more { width: calc(100% - 40px); margin: 24px 20px 0; }
        }
        @media (max-width: 500px) {
          .cat-page-hero-title { font-size: 54px; line-height: 48px; }
          .cat-page-hero-sub { font-size: 17px; }
          .cat-page-grid { grid-template-columns: 1fr 1fr; margin: 0 10px; background: #000; gap: 12px; }
          .cat-page-load-more { width: calc(100% - 20px); margin: 20px 10px 0; }
          .cat-page-filters { gap: 8px; padding: 16px 10px; }
          .filter-btn { font-size: 15px; padding: 9px 14px; }
        }
      `}</style>

      <section className="cat-page-hero">
        <img src={heroImg} alt="" className="cat-page-hero-bg" />
        <div className="cat-page-hero-overlay" />
        <Navbar />
        <div className="cat-page-hero-content">
          <div className="cat-page-hero-inner">
            <div className="cat-page-hero-line" />
            <div className="cat-page-hero-text">
              <h1 className="cat-page-hero-title">{title}</h1>
              {desc && <p className="cat-page-hero-sub">{desc}</p>}
            </div>
          </div>
        </div>
      </section>

      <section className="cat-page-section">
        {allFilters.length > 0 && (
          <div className="cat-page-filters">
            {allFilters.map(f => (
              <button
                key={f}
                className={`filter-btn ${activeFilter === f ? 'filter-active' : 'filter-inactive'}`}
                onClick={() => f === 'Tutti' ? setSearchParams({}) : setSearchParams({ filtro: f })}
              >{f}</button>
            ))}
          </div>
        )}

        {loading && products.length === 0 ? (
          <div className="cat-page-loading">Caricamento prodotti...</div>
        ) : products.length === 0 ? (
          <div className="cat-page-empty">
            Nessun prodotto trovato.<br />
            <span style={{fontSize:15,marginTop:8,display:'block'}}>Configura i prodotti in WooCommerce per vederli qui.</span>
          </div>
        ) : (
          <>
            <div className="cat-page-grid">
              {products.map(p => <ProductCard key={p.id} product={p} />)}
            </div>
            {hasMore && (
              <button className="cat-page-load-more" onClick={() => { const n = page+1; setPage(n); loadProducts(n, false, activeCatId); }}>
                {loading ? 'Caricamento...' : 'Carica altri prodotti'}
              </button>
            )}
          </>
        )}
      </section>

      <Footer />
    </>
  );
}
